import { __extends } from "tslib";
/**
* AuthPlugin.ts
* @author Hector Hernandez (hectorh)
* @copyright Microsoft 2019
*/
import dynamicProto from "@microsoft/dynamicproto-js";
import { BaseTelemetryPlugin, _throwInternal, addPageHideEventListener, addPageUnloadEventListener, arrForEach, createProcessTelemetryContext, createUniqueNamespace, eventOff, eventOn, getCookieValue, getDocument, getWindow, mergeEvtNamespace, objDefineAccessors, onConfigChange, removePageHideEventListener, removePageUnloadEventListener, safeGetCookieMgr, setProcessTelemetryTimings, toISOString } from "@microsoft/applicationinsights-core-js";
import { isString, objDeepFreeze } from "@nevware21/ts-utils";
import { AuthType } from "./Enums";
import { WebAuthHandler } from "./WebAuthHandler";
var bouncedAuthCookieName = "authBounced";
var aadloginUrl = "https://login.microsoftonline.com";
var defaultHandShakeTimeoutMS = 5000;
var undefValue;
var defaultAuthConfig = objDeepFreeze({
    authType: undefValue,
    loggedInStatusCallback: null,
    overrideAuthEndpointUrl: undefValue,
    handShakeTimeoutMs: defaultHandShakeTimeoutMS
});
var AuthPlugin = /** @class */ (function (_super) {
    __extends(AuthPlugin, _super);
    function AuthPlugin() {
        var _this = _super.call(this) || this;
        _this.identifier = "AuthPlugin";
        _this.priority = 190;
        _this.version = '4.4.1';
        var _core;
        var _authHandler;
        var _authConfig;
        var _handshakeInProgress;
        var _authHandhshakeTimeout;
        var _authHandhshakeTimeoutMs;
        var _eventQueue;
        var _evtNamespace;
        var _endpointUrl;
        var _excludePageUnloadEvents;
        dynamicProto(AuthPlugin, _this, function (_self, _base) {
            _initDefaults();
            _self.initialize = function (coreConfig, core, extensions) {
                var _self = _this;
                _core = core;
                _super.prototype.initialize.call(_this, coreConfig, core, extensions);
                var identifier = _self.identifier;
                _evtNamespace = mergeEvtNamespace(createUniqueNamespace(identifier), core.evtNamespace && core.evtNamespace());
                _self._addHook(onConfigChange(coreConfig, function (details) {
                    var coreConfig = details.cfg;
                    var ctx = createProcessTelemetryContext(null, coreConfig, core);
                    _authConfig = ctx.getExtCfg(identifier, defaultAuthConfig);
                    _authHandhshakeTimeoutMs = _authConfig.handShakeTimeoutMs;
                    var endpointUrl = _authConfig.overrideAuthEndpointUrl ? _authConfig.overrideAuthEndpointUrl : coreConfig.endpointUrl;
                    _authHandler = isString(endpointUrl) && new WebAuthHandler(endpointUrl, _authConfig.authType, _self.diagLog());
                    _updateWParam();
                    var excludePageUnloadEvents = coreConfig.disablePageUnloadEvents || [];
                    if (_excludePageUnloadEvents && _excludePageUnloadEvents !== excludePageUnloadEvents) {
                        removePageUnloadEventListener(null, _evtNamespace);
                        removePageHideEventListener(null, _evtNamespace);
                        _excludePageUnloadEvents = null;
                    }
                    if (!_excludePageUnloadEvents) {
                        // If page is closed release queue
                        addPageUnloadEventListener(_doUnload, excludePageUnloadEvents, _evtNamespace);
                        addPageHideEventListener(_doUnload, excludePageUnloadEvents, _evtNamespace);
                    }
                    // Add listener to receive messages from Collector to determine when auth handshake is done
                    // This message will be sent from iframe to parent window
                    var win = getWindow();
                    if (isString(endpointUrl)) {
                        if (win) {
                            if (_endpointUrl && _endpointUrl !== endpointUrl) {
                                eventOff(win, "message", null, _evtNamespace);
                                _endpointUrl = null;
                            }
                            if (!_endpointUrl) {
                                eventOn(win, "message", function (msg) { _receiveMessage(msg, endpointUrl); }, _evtNamespace);
                            }
                        }
                        _endpointUrl = endpointUrl;
                    }
                    _excludePageUnloadEvents = excludePageUnloadEvents;
                }));
                _handshakeInProgress = false;
                function _doUnload() {
                    _releaseEventQueue();
                }
            };
            _self.processTelemetry = function (event, itemCtx) {
                itemCtx = _self._getTelCtx(itemCtx);
                setProcessTelemetryTimings(event, _self.identifier);
                // User is logged in
                if (_authConfig.authType !== AuthType.NONE && _authConfig.loggedInStatusCallback && _authConfig.loggedInStatusCallback() === true) {
                    var cookieMgr = safeGetCookieMgr(_self.core);
                    // Check for bounced cookie
                    var authBounced = getCookieValue(cookieMgr, bouncedAuthCookieName);
                    if (!authBounced) {
                        // Create bounced cookie to avoid multiple handshake processes
                        var halfHour = 30 * 60;
                        cookieMgr.set(bouncedAuthCookieName, toISOString(new Date()), halfHour);
                        // Start hand shake process
                        _handshakeInProgress = true;
                        // Timeout handshake if it takes more than 5 seconds
                        _authHandhshakeTimeout = setTimeout(function () { _completeAuthHandshake(); }, _authHandhshakeTimeoutMs);
                        _authHandler && _authHandler.startAuthHandshake();
                    }
                }
                if (_handshakeInProgress) {
                    _eventQueue.push({
                        ctx: itemCtx,
                        item: event
                    });
                }
                else {
                    _self.processNext(event, itemCtx);
                }
            };
            _self.signOut = function () {
                // Clean client and server side cookies
                var cookieMgr = safeGetCookieMgr(_core);
                cookieMgr.del(bouncedAuthCookieName);
                _authHandler && _authHandler.signOutAndClearCookies();
                _completeAuthHandshake();
            };
            _self.setAuthType = function (authType) {
                // Comple handshake if triggered before
                _completeAuthHandshake();
                _authConfig.authType = authType;
                _authHandler && _authHandler.setAuthType(authType);
                _updateWParam();
            };
            _self._doTeardown = function (unloadCtx, unloadState) {
                _completeAuthHandshake();
                eventOff(getWindow(), "message", null, _evtNamespace);
                removePageUnloadEventListener(null, _evtNamespace);
                removePageHideEventListener(null, _evtNamespace);
                // Just register to remove all events associated with this namespace
                _initDefaults();
            };
            /**
             * Add w parameter used by Collector to identify Web Requests will try to get auth properties
             */
            function _updateWParam() {
                if (_authConfig.authType != AuthType.NONE) {
                    var extCore_1 = _core;
                    var existingGetWParamMethod = extCore_1.getWParam;
                    extCore_1.getWParam = function () {
                        var wparam = 0;
                        wparam = _authConfig.authType === AuthType.MSA ? wparam | 8 : wparam | 16;
                        return wparam | (existingGetWParamMethod ? existingGetWParamMethod.call(extCore_1) : 0);
                    };
                }
            }
            /**
             * Attaches to message, to receive responses from Collector.
             */
            function _receiveMessage(event, endpointUrl) {
                var doc = getDocument();
                if (doc) {
                    var a = doc.createElement("a");
                    a.href = endpointUrl;
                    var endpointOrigin = a.protocol + "//" + a.hostname;
                    // login.microsoftonline.com is workaround for IE10 where the origin is the iframe of origin not origin of request
                    if (event.origin === endpointOrigin || event.origin === aadloginUrl) {
                        if (event.data) {
                            try {
                                _completeAuthHandshake();
                            }
                            catch (e) {
                                _throwInternal(_self.diagLog(), 1 /* eLoggingSeverity.CRITICAL */, 502 /* _eExtendedInternalMessageId.AuthRedirectFail */, "Error receiving auth redirect message: " + e);
                            }
                        }
                    }
                }
            }
            /**
            * Complete auth handhshake, cleanup and release the event queue
            */
            function _completeAuthHandshake() {
                if (_authHandhshakeTimeout) {
                    clearTimeout(_authHandhshakeTimeout);
                }
                if (_authHandler) {
                    // Remove auth iFrame if present
                    _authHandler.cleanAuthIFrame();
                }
                _handshakeInProgress = false;
                _releaseEventQueue();
            }
            /**
            * Release internal event queue
            */
            function _releaseEventQueue() {
                arrForEach(_eventQueue, function (evt) {
                    evt.ctx.processNext(evt.item);
                });
                _eventQueue = [];
            }
            function _initDefaults() {
                _core = null;
                _authHandler = null;
                _authConfig = null;
                _handshakeInProgress = false;
                _authHandhshakeTimeout = null;
                _authHandhshakeTimeoutMs = null;
                _eventQueue = [];
            }
            // For backward compatibility
            objDefineAccessors(_self, "_authHandler", function () { return _authHandler; });
            objDefineAccessors(_self, "_authConfig", function () { return _authConfig; });
            objDefineAccessors(_self, "_eventQueue", function () { return _eventQueue; });
            objDefineAccessors(_self, "_handshakeInProgress", function () { return _handshakeInProgress; });
            objDefineAccessors(_self, "_evtNamespace", function () { return "." + _evtNamespace; });
        });
        return _this;
    }
    AuthPlugin.prototype.initialize = function (coreConfig, core, extensions) {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
    };
    /**
     * Process the event and add it to an internal queue if handshake in process
     * @param event - The event to process
     * @param itemCtx - This is the context for the current request, ITelemetryPlugin instances
     * can optionally use this to access the current core instance or define / pass additional information
     * to later plugins (vs appending items to the telemetry item)
     */
    AuthPlugin.prototype.processTelemetry = function (event, itemCtx) {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
    };
    /**
    * Clear 1DS authentication cookies
    */
    AuthPlugin.prototype.signOut = function () {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
    };
    /**
    * Set auth type and clen authHandler is already started
    * @param authType - The authentication method.  1-MSA, 2-AAD.
    */
    AuthPlugin.prototype.setAuthType = function (authType) {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
    };
    return AuthPlugin;
}(BaseTelemetryPlugin));
export { AuthPlugin };
//# sourceMappingURL=AuthPlugin.js.map